<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'supplier-invoices' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Détails de la facture</h1>
          <p v-if="invoice && invoice.invoice_number" class="text-xs text-gray-400">{{ invoice.invoice_number }}</p>
        </div>
      </div>
      <div v-if="invoice && invoice.id" class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="downloadPdf">
          <i class="fas fa-download text-[11px]"></i>
          Télécharger
        </button>
        <button
          v-if="invoice.status !== 'paid' && invoice.status !== 'cancelled'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="showPaymentModal = true">
          <i class="fas fa-plus text-[10px]"></i>
          Ajouter un paiement
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement des détails...</p>
    </div>

    <!-- Facture introuvable -->
    <div v-else-if="!invoice || !invoice.id" class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-exclamation-circle text-4xl"></i>
      </div>
      <h2 class="text-base font-semibold text-gray-700 mb-1">Facture introuvable</h2>
      <p class="text-sm text-gray-400 mb-6">Cette facture n'existe pas ou a été supprimée</p>
      <button
        class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="$router.push({ name: 'supplier-invoices' })">
        <i class="fas fa-arrow-left mr-1.5 text-xs"></i>
        Retour à la liste
      </button>
    </div>

    <!-- Contenu principal -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte info facture -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-start justify-between mb-5">
            <div>
              <h2 class="text-base font-bold text-gray-900 font-mono">{{ invoice.invoice_number }}</h2>
              <p class="text-xs text-gray-400 mt-0.5">
                <i class="fas fa-calendar mr-1 text-[10px]"></i>
                {{ formatDate(invoice.invoice_date) }}
              </p>
            </div>
            <span :class="[
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
              getStatusVariant(invoice.status) === 'warning' ? 'bg-orange-50 text-orange-600' :
              getStatusVariant(invoice.status) === 'success' ? 'bg-green-50 text-green-600' :
              getStatusVariant(invoice.status) === 'info' ? 'bg-blue-50 text-blue-600' :
              getStatusVariant(invoice.status) === 'danger' ? 'bg-red-50 text-red-600' :
              'bg-gray-100 text-gray-500'
            ]">
              <span class="w-1.5 h-1.5 rounded-full bg-current"></span>
              {{ getStatusLabel(invoice.status) }}
            </span>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Fournisseur</p>
              <p class="text-sm font-medium text-gray-900">{{ invoice.supplier?.name || '-' }}</p>
              <div v-if="invoice.supplier?.phone || invoice.supplier?.email" class="mt-1 space-y-0.5">
                <p v-if="invoice.supplier?.phone" class="text-xs text-gray-400">
                  <i class="fas fa-phone mr-1 text-[10px]"></i>{{ invoice.supplier.phone }}
                </p>
                <p v-if="invoice.supplier?.email" class="text-xs text-gray-400">
                  <i class="fas fa-envelope mr-1 text-[10px]"></i>{{ invoice.supplier.email }}
                </p>
              </div>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Commande d'achat</p>
              <div v-if="invoice.purchase_order">
                <p class="text-sm font-medium text-gray-900">{{ invoice.purchase_order.reference }}</p>
                <button
                  class="text-xs text-primary hover:text-primary-dark transition-colors mt-0.5"
                  @click="$router.push({ name: 'purchase-order-details', params: { id: invoice.purchase_order.id } })">
                  Voir la commande <i class="fas fa-arrow-right text-[10px] ml-0.5"></i>
                </button>
              </div>
              <p v-else class="text-sm text-gray-400">Aucune commande associée</p>
            </div>
            <div v-if="invoice.notes" class="col-span-2 md:col-span-1">
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Remarques</p>
              <div class="bg-gray-50 rounded-lg p-3 mt-1">
                <p class="text-sm text-gray-600 whitespace-pre-line">{{ invoice.notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Commandes associées -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-4 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">
              Commandes associées
              <span class="text-gray-400 font-normal">({{ invoiceOrders.length }})</span>
            </h3>
          </div>

          <div v-if="invoiceOrders.length === 0" class="p-8 text-center text-sm text-gray-400">
            Aucune commande associée à cette facture.
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full min-w-[600px]">
              <thead class="bg-gray-50/80">
                <tr>
                  <th class="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Référence</th>
                  <th class="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Date</th>
                  <th class="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-28">Statut</th>
                  <th class="px-3 py-2.5 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-32">Montant</th>
                  <th class="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50">
                <tr v-for="order in invoiceOrders" :key="order.id"
                  class="hover:bg-gray-50/60 transition-colors cursor-pointer"
                  @click="goToPurchaseOrder(order.id)">
                  <td class="px-5 py-3">
                    <p class="text-sm font-medium text-gray-900">{{ order.reference || order.number || '-' }}</p>
                    <p class="text-xs text-gray-400 mt-0.5">{{ order.supplier?.name || invoice.supplier?.name || '-' }}</p>
                  </td>
                  <td class="px-3 py-3">
                    <span class="text-xs text-gray-500">{{ formatDate(order.order_date || order.date) }}</span>
                  </td>
                  <td class="px-3 py-3 text-center">
                    <span :class="[
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold',
                      getOrderStatusVariant(order.status) === 'success' ? 'bg-green-50 text-green-600' :
                      getOrderStatusVariant(order.status) === 'warning' ? 'bg-orange-50 text-orange-600' :
                      getOrderStatusVariant(order.status) === 'danger' ? 'bg-red-50 text-red-600' :
                      'bg-gray-100 text-gray-500'
                    ]">
                      <span class="w-1.5 h-1.5 rounded-full bg-current"></span>
                      {{ getOrderStatusLabel(order.status) }}
                    </span>
                  </td>
                  <td class="px-3 py-3 text-right">
                    <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(getOrderTotal(order)) }}</span>
                  </td>
                  <td class="px-3 py-3 text-center">
                    <button
                      class="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                      @click.stop="goToPurchaseOrder(order.id)" title="Voir">
                      <i class="fas fa-eye text-[11px]"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé financier -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden lg:sticky lg:top-5">
          <div class="px-5 py-3 bg-primary/5 border-b border-primary/10">
            <h3 class="text-sm font-semibold text-primary text-center">Résumé financier</h3>
          </div>
          <div class="p-5 space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">
                <i class="fas fa-file-invoice mr-1 text-[10px]"></i>
                Montant total
              </span>
              <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(invoice.total_amount) }}</span>
            </div>

            <div class="h-px bg-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">
                <i class="fas fa-circle-check mr-1 text-[10px]"></i>
                Montant payé
              </span>
              <span class="text-sm font-semibold text-green-600">{{ formatCurrency(invoice.paid_amount) }}</span>
            </div>

            <div class="h-px bg-gray-100"></div>

            <div class="flex justify-between items-center pt-1">
              <span class="text-xs font-medium text-gray-500">
                <i class="fas fa-wallet mr-1 text-[10px]"></i>
                Solde restant
              </span>
              <span :class="[
                'text-lg font-bold',
                remainingAmount > 0 ? 'text-amber-500' : 'text-green-600'
              ]">{{ formatCurrency(remainingAmount) }}</span>
            </div>
          </div>

          <div v-if="remainingAmount > 0 && invoice.status !== 'cancelled'" class="px-5 py-4 border-t border-gray-100">
            <button
              class="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
              @click="showPaymentModal = true">
              <i class="fas fa-plus text-xs"></i>
              Ajouter un paiement
            </button>
          </div>
        </div>

        <!-- Historique paiements -->
        <div v-if="payments.length > 0" class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 class="text-sm font-semibold text-gray-700">Historique des paiements</h3>
            <span class="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{{ payments.length }}</span>
          </div>

          <div class="divide-y divide-gray-50">
            <div v-for="payment in payments" :key="payment.id" class="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/60 transition-colors">
              <div class="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-circle-check text-green-500 text-[11px]"></i>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900">{{ getPaymentMethodLabel(payment.payment_method) }}</p>
                <p class="text-[11px] text-gray-400">{{ formatDate(payment.payment_date) }}</p>
                <p v-if="payment.reference" class="text-[11px] text-gray-300">Réf: {{ payment.reference }}</p>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <span class="text-sm font-bold text-green-600">{{ formatCurrency(payment.amount) }}</span>
                <button v-if="canDeletePayment"
                  class="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  @click="confirmDeletePayment(payment.id)" title="Supprimer">
                  <i class="fas fa-trash text-[10px]"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de paiement -->
    <transition name="modal">
      <div v-if="showPaymentModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5" @click="showPaymentModal = false">
        <div class="bg-white rounded-xl max-w-lg w-full shadow-xl" @click.stop>
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 class="text-base font-semibold text-gray-900">Ajouter un paiement</h3>
            <button class="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-gray-500 border border-gray-200 rounded-lg transition-colors"
              @click="showPaymentModal = false">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
          <div class="p-6 space-y-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Montant <span class="text-red-500">*</span></p>
              <input type="number" v-model="paymentForm.amount" placeholder="Montant du paiement" :max="remainingAmount"
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
              <p class="text-[11px] text-gray-300 mt-1">Solde restant: {{ formatCurrency(remainingAmount) }}</p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Méthode de paiement <span class="text-red-500">*</span></p>
              <select v-model="paymentForm.payment_method"
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors">
                <option value="">Sélectionner...</option>
                <option value="cash">Espèces</option>
                <option value="check">Chèque</option>
                <option value="bank_transfer">Virement bancaire</option>
                <option value="mobile_money">Mobile Money</option>
                <option value="credit_card">Carte bancaire</option>
                <option value="other">Autre</option>
              </select>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Date du paiement <span class="text-red-500">*</span></p>
              <input type="date" v-model="paymentForm.payment_date"
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Référence (optionnelle)</p>
              <input type="text" v-model="paymentForm.reference" placeholder="N° de chèque, référence virement..."
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Notes (optionnelles)</p>
              <textarea v-model="paymentForm.notes" rows="2" placeholder="Notes additionnelles..."
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"></textarea>
            </div>
          </div>
          <div class="flex gap-3 px-6 pb-6">
            <button class="flex-1 px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              @click="showPaymentModal = false">
              Annuler
            </button>
            <button
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              @click="submitPayment" :disabled="isSubmitting">
              <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-[10px]"></i>
              <span v-else>Enregistrer</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import supplierInvoiceService from '@/services/supplierInvoiceService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'SupplierInvoiceDetailsView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toastStore = useToastStore()

    const invoice = ref(null)
    const payments = ref([])
    const loading = ref(true)
    const isSubmitting = ref(false)
    const showPaymentModal = ref(false)

    const paymentForm = ref({
      amount: null,
      payment_method: '',
      payment_date: new Date().toISOString().split('T')[0],
      reference: '',
      notes: ''
    })

    const canDeletePayment = computed(() => {
      return true
    })

    const remainingAmount = computed(() => {
      const total = Number(invoice.value?.total_amount) || 0
      const paid = Number(invoice.value?.paid_amount) || 0
      return Math.max(0, total - paid)
    })

    const invoiceOrders = computed(() => {
      const orders = invoice.value?.purchase_orders || invoice.value?.purchaseOrders || invoice.value?.orders || []
      if (Array.isArray(orders) && orders.length > 0) return orders
      if (invoice.value?.purchase_order) return [invoice.value.purchase_order]
      return []
    })

    const loadInvoice = async () => {
      loading.value = true
      try {
        const id = route.params.id
        const res = await supplierInvoiceService.getSupplierInvoice(id)
        invoice.value = res.data?.data || res.data || {}
        invoice.value.items = invoice.value.items || []

        const paymentsRes = await supplierInvoiceService.getPayments(id)
        payments.value = paymentsRes.data?.data || paymentsRes.data || []
      } catch (e) {
        console.error('Error loading invoice:', e)
        invoice.value = null
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement de la facture',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const submitPayment = async () => {
      if (!paymentForm.value.amount || !paymentForm.value.payment_method) {
        toastStore.addToast({
          type: 'error',
          message: 'Veuillez remplir tous les champs obligatoires',
          duration: 3000
        })
        return
      }

      if (Number(paymentForm.value.amount) > remainingAmount.value) {
        toastStore.addToast({
          type: 'error',
          message: 'Le montant ne peut pas dépasser le solde restant',
          duration: 3000
        })
        return
      }

      try {
        isSubmitting.value = true
        await supplierInvoiceService.addPayment(invoice.value.id, paymentForm.value)
        toastStore.addToast({
          type: 'success',
          message: 'Paiement ajouté avec succès',
          duration: 3000
        })
        showPaymentModal.value = false
        paymentForm.value = {
          amount: null,
          payment_method: '',
          payment_date: new Date().toISOString().split('T')[0],
          reference: '',
          notes: ''
        }
        await loadInvoice()
      } catch (e) {
        console.error('Error adding payment:', e)
        toastStore.addToast({
          type: 'error',
          message: e.response?.data?.message || 'Erreur lors de l\'ajout du paiement',
          duration: 3000
        })
      } finally {
        isSubmitting.value = false
      }
    }

    const confirmDeletePayment = async (paymentId) => {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) return

      try {
        await supplierInvoiceService.deletePayment(invoice.value.id, paymentId)
        toastStore.addToast({
          type: 'success',
          message: 'Paiement supprimé avec succès',
          duration: 3000
        })
        await loadInvoice()
      } catch (e) {
        console.error('Error deleting payment:', e)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de la suppression du paiement',
          duration: 3000
        })
      }
    }

    const downloadPdf = async () => {
      try {
        await supplierInvoiceService.downloadSupplierInvoicePdf(invoice.value.id)
        toastStore.addToast({
          type: 'success',
          message: 'Téléchargement en cours...',
          duration: 3000
        })
      } catch (e) {
        console.error('Error downloading PDF:', e)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du téléchargement du PDF',
          duration: 3000
        })
      }
    }

    const getStatusLabel = (status) => {
      return supplierInvoiceService.getStatusLabel(status)
    }

    const getStatusVariant = (status) => {
      const map = {
        pending: 'warning',
        paid: 'success',
        partial: 'info',
        partial_paid: 'info',
        cancelled: 'danger',
        overdue: 'danger'
      }
      return map[status] || 'gray'
    }

    const getPaymentMethodLabel = (method) => {
      return supplierInvoiceService.getPaymentMethodLabel(method)
    }

    const getOrderStatusLabel = (status) => {
      const labels = {
        active: 'Active',
        inactive: 'Inactive',
        pending: 'En attente',
        completed: 'Terminée',
        received: 'Reçue',
        cancelled: 'Annulée'
      }
      return labels[status] || status || '-'
    }

    const getOrderStatusVariant = (status) => {
      const map = {
        active: 'success',
        inactive: 'gray',
        pending: 'warning',
        completed: 'success',
        received: 'success',
        cancelled: 'danger'
      }
      return map[status] || 'gray'
    }

    const getOrderTotal = (order) => {
      if (!order) return 0
      const directTotal =
        order.total_amount ?? order.amount ?? order.total ?? order.grand_total ?? order.subtotal
      if (directTotal !== undefined && directTotal !== null) return directTotal

      const items = order.items || order.purchase_order_items || order.sale_items || []
      if (!Array.isArray(items)) return 0

      return items.reduce((sum, item) => {
        const qty = Number(item.quantity ?? item.qty ?? 0)
        const price = Number(item.purchase_price ?? item.price ?? item.unit_price ?? 0)
        const discount = Number(item.discount ?? 0)
        return sum + qty * price - discount
      }, 0)
    }

    const formatCurrency = (amount) => {
      return supplierInvoiceService.formatCurrency(amount)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return supplierInvoiceService.formatDate(dateString)
    }

    const formatDateTime = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const formatDateShort = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit'
      })
    }

    const formatTimeShort = (dateString) => {
      if (!dateString) return '-'
      const d = new Date(dateString)
      return d.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getInitials = (name) => {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    }

    const goToPurchaseOrder = (id) => {
      if (!id) return
      router.push({ name: 'purchase-order-details', params: { id } })
    }

    onMounted(loadInvoice)

    return {
      invoice,
      payments,
      loading,
      isSubmitting,
      showPaymentModal,
      paymentForm,
      canDeletePayment,
      remainingAmount,
      invoiceOrders,
      submitPayment,
      confirmDeletePayment,
      downloadPdf,
      getStatusLabel,
      getStatusVariant,
      getPaymentMethodLabel,
      getOrderStatusLabel,
      getOrderStatusVariant,
      getOrderTotal,
      formatCurrency,
      formatDate,
      formatDateShort,
      formatTimeShort,
      formatDateTime,
      getInitials,
      goToPurchaseOrder
    }
  }
})
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
